import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle, Camera, CameraOff, Clock, Mic, MicOff, Video,
} from 'lucide-react';

import MeetingInterviewPanel from './MeetingInterviewPanel';
import { errorFromResponse, networkErrorMessage, readJson, swallowNetworkRejections } from '../portalFetch';
import { formatWaitClock, startTimeMs } from '../scheduleClock';
import { BrandWordmark } from '../../../shared/components/BrandLogo';

const API = `${process.env.REACT_APP_API_URL || 'https://recruitai.pingitright.com/api'}/portal`;

const styles = {
  page: { minHeight: '100vh', background: '#0f172a', fontFamily: "'Inter', system-ui, sans-serif", color: '#e2e8f0' },
  header: {
    background: 'rgba(15,23,42,0.92)',
    borderBottom: '1px solid #1e293b',
    padding: '0 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 64,
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  brand: { fontSize: 16, fontWeight: 800, letterSpacing: 0.2 },
  brandSub: { fontSize: 11, color: '#94a3b8' },
  wrap: { maxWidth: 1180, margin: '0 auto', padding: '28px 20px 60px' },
  grid: { display: 'grid', gridTemplateColumns: 'minmax(0, 1.05fr) minmax(320px, 1.1fr)', gap: 20 },
  card: {
    background: '#111827',
    border: '1px solid #1f2937',
    borderRadius: 16,
    padding: 22,
  },
  videoShell: {
    position: 'relative',
    background: '#020617',
    borderRadius: 14,
    overflow: 'hidden',
    aspectRatio: '16 / 10',
    border: '1px solid #1e293b',
  },
  video: { width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)' },
  meterTrack: {
    height: 8,
    background: '#1e293b',
    borderRadius: 99,
    overflow: 'hidden',
    marginTop: 10,
  },
  btn: {
    width: '100%',
    padding: '12px 16px',
    border: 'none',
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
  },
};

function hintFromProctor(status) {
  if (!status?.live) return null;
  if (status.unavailable) return { ok: false, idle: true, label: 'Face check unavailable' };
  if (status.cameraOff || status.faces === 0) return { ok: false, label: 'No face' };
  if (status.faces >= 2) return { ok: false, label: `${status.faces} faces` };
  if (status.lookingAway) return { ok: false, label: 'Looking away' };
  return { ok: true, label: 'Face OK' };
}

function thankYouPath(token) {
  const path = window.location.pathname || '';
  if (path.includes('/portal/meeting/')) return `/portal/meeting/${token}/thank-you`;
  return `/interview/meeting/${token}/thank-you`;
}

function formatWhen(room) {
  const slot = room?.interview_slot;
  if (slot && !/^\d{4}-\d{2}-\d{2}T/.test(slot)) return slot;
  const raw = (/^\d{4}-\d{2}-\d{2}T/.test(slot || '') ? slot : null) || room?.scheduled_at;
  if (!raw) return slot || '—';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleString('en-IN', {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function waitMessage(msLeft, canJoin, lateInfo) {
  if (msLeft > 0) {
    return canJoin
      ? `You can wait in the room. AI interview starts in ${formatWaitClock(msLeft)}.`
      : `AI interview starts in ${formatWaitClock(msLeft)}. Stay on this page — it will start automatically.`;
  }
  if (lateInfo?.msUntilClose > 0) {
    return `You're a few minutes late. The interview still starts from the beginning — join within ${formatWaitClock(lateInfo.msUntilClose)} or the room will close.`;
  }
  return 'Interview time reached. Starting now…';
}

function closeTimeMs(room) {
  const fromApi = startTimeMs(room?.join_closes_at);
  if (Number.isFinite(fromApi)) return fromApi;
  const startAt = startTimeMs(room?.scheduled_at);
  const lateMin = Number(room?.join_late_minutes) || 15;
  return Number.isFinite(startAt) ? startAt + lateMin * 60 * 1000 : NaN;
}

export default function PortalMeeting() {
  const { token } = useParams();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const analyserRef = useRef(null);
  const audioCtxRef = useRef(null);
  const micSourceRef = useRef(null);
  const rafRef = useRef(null);

  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [phase, setPhase] = useState('setup'); // setup | joined
  const [cameraOn, setCameraOn] = useState(true);
  const [micOn, setMicOn] = useState(true);
  const [cameraOk, setCameraOk] = useState(false);
  const [micOk, setMicOk] = useState(false);
  const [micLevel, setMicLevel] = useState(0);
  const [proctorHint, setProctorHint] = useState(null);
  const [saving, setSaving] = useState(false);
  const [joining, setJoining] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [hasStream, setHasStream] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const joiningRef = useRef(false);
  const autoJoinRef = useRef(false);
  const joinRoomRef = useRef(async () => {});
  const setupReadyRef = useRef(false);
  const phaseRef = useRef(phase);
  const roomRef = useRef(null);
  roomRef.current = room;

  const stopStream = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    setHasStream(false);
    analyserRef.current = null;
    try { micSourceRef.current?.disconnect(); } catch (_) { /* ignore */ }
    micSourceRef.current = null;
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
  }, []);

  const openThankYou = useCallback(() => {
    swallowNetworkRejections();
    const current = roomRef.current;
    navigate(thankYouPath(token), {
      replace: true,
      state: {
        candidateName: current?.candidate_name || '',
        jobTitle: current?.job_title || '',
      },
    });
  }, [navigate, token]);

  useEffect(() => () => stopStream(), [stopStream]);

  const loadRoom = useCallback(() => {
    if (!token) {
      setError('Invalid meeting link.');
      setLoading(false);
      return;
    }
    fetch(`${API}/meeting/${token}`)
      .then(async (r) => {
        const data = await readJson(r);
        if (!r.ok) throw new Error(errorFromResponse(data, 'Meeting room not found.'));
        return data;
      })
      .then((data) => {
        setRoom(data);
        if (!streamRef.current) {
          setCameraOk(Boolean(data.camera_ok));
          setMicOk(Boolean(data.mic_ok));
        }
        if (data.interview_completed) {
          setPhase('setup');
        } else if (data.status === 'joined') {
          setPhase('joined');
        }
        setLoading(false);
      })
      .catch((err) => {
        setError(networkErrorMessage(err, 'Failed to load meeting room.') || 'Failed to load meeting room.');
        setLoading(false);
      });
  }, [token]);

  useEffect(() => { loadRoom(); }, [loadRoom]);

  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 250);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!token || phase === 'joined') return undefined;
    const id = setInterval(loadRoom, 15000);
    return () => clearInterval(id);
  }, [token, loadRoom, phase]);

  useEffect(() => {
    if (phase !== 'joined') return undefined;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    try { micSourceRef.current?.disconnect(); } catch (_) { /* ignore */ }
    micSourceRef.current = null;
    analyserRef.current = null;
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
    return undefined;
  }, [phase]);

  const watchMic = useCallback((stream) => {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    audioCtxRef.current = ctx;
    const source = ctx.createMediaStreamSource(stream);
    micSourceRef.current = source;
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    source.connect(analyser);
    analyserRef.current = analyser;
    const data = new Uint8Array(analyser.frequencyBinCount);

    const tick = () => {
      analyser.getByteFrequencyData(data);
      const avg = data.reduce((a, b) => a + b, 0) / (data.length || 1);
      setMicLevel(Math.min(100, Math.round((avg / 80) * 100)));
      if (avg > 8) setMicOk(true);
      rafRef.current = requestAnimationFrame(tick);
    };
    tick();
  }, []);

  const enableDevices = async () => {
    setError('');
    try {
      stopStream();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1920, min: 640 },
          height: { ideal: 1080, min: 360 },
          frameRate: { ideal: 30, min: 15 },
          facingMode: 'user',
          aspectRatio: { ideal: 1.777777778 },
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: { ideal: 1 },
        },
      });
      stream.getVideoTracks().forEach((track) => {
        track.contentHint = 'motion';
        track.applyConstraints({
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          frameRate: { ideal: 30 },
        }).catch(() => {});
      });
      stream.getAudioTracks().forEach((track) => {
        track.contentHint = 'speech';
      });
      streamRef.current = stream;
      setHasStream(true);
      if (videoRef.current) videoRef.current.srcObject = stream;
      setCameraOk(Boolean(stream.getVideoTracks().length));
      if (stream.getAudioTracks().length) setMicOk(true);
      setCameraOn(true);
      setMicOn(true);
      watchMic(stream);
    } catch (_) {
      setError('Camera or microphone access was denied. Allow both to continue setup.');
      setCameraOk(false);
      setMicOk(false);
    }
  };

  useEffect(() => {
    if (videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [phase, cameraOk]);

  useEffect(() => {
    if (phase === 'joined' && !hasStream) {
      enableDevices();
    }
  }, [phase, hasStream]);

  const toggleCamera = () => {
    const next = !cameraOn;
    streamRef.current?.getVideoTracks().forEach(t => { t.enabled = next; });
    setCameraOn(next);
  };

  const toggleMic = () => {
    const next = !micOn;
    streamRef.current?.getAudioTracks().forEach(t => { t.enabled = next; });
    setMicOn(next);
  };

  const saveSetup = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API}/meeting/${token}/setup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ camera_ok: cameraOk, mic_ok: micOk }),
      });
      const data = await readJson(res);
      if (!res.ok) throw new Error(errorFromResponse(data, 'Could not save setup.'));
      setRoom(data);
      return data;
    } catch (err) {
      const msg = networkErrorMessage(err, 'Could not save setup.');
      if (msg) setError(msg);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const joinRoom = async () => {
    if (!cameraOk || !micOk) {
      setError('Turn on camera and microphone, then complete setup before joining.');
      return;
    }
    const closeAt = closeTimeMs(room);
    if (room?.interview_completed) {
      setError('This interview is already complete. The link has expired.');
      loadRoom();
      return;
    }
    if (room?.too_late || (Number.isFinite(closeAt) && Date.now() >= closeAt)) {
      setError('This interview room has closed. Please contact the recruiter for a new slot.');
      loadRoom();
      return;
    }
    if (joiningRef.current) return;
    joiningRef.current = true;
    setJoining(true);
    setError('');
    try {
      await saveSetup();
      const res = await fetch(`${API}/meeting/${token}/join`, { method: 'POST' });
      const data = await readJson(res);
      if (!res.ok) throw new Error(errorFromResponse(data, 'Could not join the room.'));
      setRoom(data);
      setPhase('joined');
    } catch (err) {
      autoJoinRef.current = false;
      const msg = networkErrorMessage(err, 'Could not join the room.');
      if (msg) setError(msg);
      loadRoom();
    } finally {
      joiningRef.current = false;
      setJoining(false);
    }
  };

  joinRoomRef.current = joinRoom;
  phaseRef.current = phase;
  setupReadyRef.current = Boolean(cameraOk && micOk);

  useEffect(() => {
    if (!room || room.interview_completed || room.too_late || phase === 'joined') return undefined;
    const closeAt = closeTimeMs(room);
    if (Number.isFinite(closeAt) && Date.now() >= closeAt) return undefined;
    if (!cameraOk || !micOk) return undefined;
    const startAt = startTimeMs(room.scheduled_at);
    const delay = Number.isFinite(startAt) ? startAt - Date.now() : 0;
    if (delay > 250) {
      const id = setTimeout(() => {
        if (phaseRef.current === 'joined' || autoJoinRef.current || !setupReadyRef.current) return;
        autoJoinRef.current = true;
        joinRoomRef.current();
      }, delay);
      return () => clearTimeout(id);
    }
    if (autoJoinRef.current || joiningRef.current) return undefined;
    autoJoinRef.current = true;
    joinRoomRef.current();
    return undefined;
  }, [room, phase, cameraOk, micOk]);

  const leaveRoom = async () => {
    if (leaving) return;
    setLeaving(true);
    setError('');
    autoJoinRef.current = false;
    try {
      const res = await fetch(`${API}/meeting/${token}/interview/complete`, { method: 'POST' });
      const data = await readJson(res);
      if (!res.ok) throw new Error(errorFromResponse(data, 'Could not complete the interview.'));
      stopStream();
      openThankYou();
    } catch (err) {
      const msg = networkErrorMessage(err, 'Could not complete the interview.');
      if (msg) setError(msg);
      setLeaving(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={{ ...styles.wrap, textAlign: 'center', paddingTop: 80, color: '#94a3b8' }}>Loading meeting room…</div>
      </div>
    );
  }

  if (!room) {
    return (
      <div style={styles.page}>
        <div style={styles.wrap}>
          <div style={{ ...styles.card, maxWidth: 520, margin: '80px auto', textAlign: 'center' }}>
            <AlertTriangle size={28} color="#f87171" />
            <h2 style={{ marginBottom: 8 }}>Meeting link invalid</h2>
            <p style={{ color: '#94a3b8', margin: 0 }}>{error || 'This interview room was not found.'}</p>
          </div>
        </div>
      </div>
    );
  }

  if (room.interview_completed) {
    return (
      <Navigate
        to={thankYouPath(token)}
        replace
        state={{
          candidateName: room.candidate_name || '',
          jobTitle: room.job_title || '',
        }}
      />
    );
  }

  const setupReady = cameraOk && micOk;
  const startAt = startTimeMs(room.scheduled_at);
  const closeAt = closeTimeMs(room);
  const msLeft = Number.isFinite(startAt) ? startAt - nowMs : 0;
  const msUntilClose = Number.isFinite(closeAt) ? closeAt - nowMs : 0;
  const timeReached = !Number.isFinite(startAt) || msLeft <= 0;
  const windowClosed = phase !== 'joined' && (
    Boolean(room.too_late) || (Number.isFinite(closeAt) && nowMs >= closeAt)
  );
  const lateGrace = timeReached && msUntilClose > 0 && !windowClosed;
  const lateMin = room.join_late_minutes || 15;
  const joinLabel = windowClosed
    ? 'Interview window closed'
    : !timeReached
      ? `Starts in ${formatWaitClock(msLeft)}`
      : 'Join interview room';

  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <div>
          <div style={{ ...styles.brand, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <BrandWordmark size={16} weight={800} color="inherit" /> Interview Room
          </div>
          <div style={styles.brandSub}>{room.job_title || 'AI screening interview'}</div>
        </div>
        <div style={{ fontSize: 12, color: '#94a3b8' }}>Room {room.room_code}</div>
      </header>

      <div style={styles.wrap}>
        {error && (
          <div style={{
            background: 'rgba(248,113,113,0.12)', border: '1px solid rgba(248,113,113,0.35)',
            color: '#fca5a5', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 13,
          }}>{error}</div>
        )}

        {windowClosed ? (
          <div style={{ ...styles.card, maxWidth: 560, margin: '40px auto', textAlign: 'center' }}>
            <Clock size={28} color="#fbbf24" />
            <h2>This interview window has closed</h2>
            <p style={{ color: '#94a3b8' }}>
              Scheduled for <strong style={{ color: '#e2e8f0' }}>{formatWhen(room)}</strong>.
              You can join up to {lateMin} minutes after the start — after that the room closes.
              Contact the recruitment team for a new meeting room link.
            </p>
          </div>
        ) : (
          <div style={styles.grid} className="meeting-grid">
            <section style={styles.card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                <div>
                  <div style={{ fontSize: 13, color: '#94a3b8' }}>{phase === 'joined' ? 'You are in the room' : 'Device setup'}</div>
                  <h2 style={{ margin: '4px 0 0', fontSize: 22 }}>{room.candidate_name || 'Candidate'}</h2>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" onClick={toggleMic} disabled={!hasStream} style={{
                    ...styles.btn, width: 'auto', background: micOn ? '#1e293b' : '#7f1d1d', color: '#fff',
                  }}>{micOn ? <Mic size={14} /> : <MicOff size={14} />} {micOn ? 'Mute' : 'Unmute'}</button>
                  <button type="button" onClick={toggleCamera} disabled={!hasStream} style={{
                    ...styles.btn, width: 'auto', background: cameraOn ? '#1e293b' : '#7f1d1d', color: '#fff',
                  }}>{cameraOn ? <Camera size={14} /> : <CameraOff size={14} />}</button>
                </div>
              </div>

              <div style={styles.videoShell}>
                <video ref={videoRef} autoPlay playsInline muted style={{
                  ...styles.video,
                  display: cameraOn && hasStream ? 'block' : 'none',
                }} />
                {phase === 'joined' && hasStream && (
                  <div style={{
                    position: 'absolute', top: 10, left: 10, background: '#b91c1c',
                    color: '#fff', fontSize: 11, fontWeight: 800, padding: '3px 8px', borderRadius: 6,
                    letterSpacing: 0.6,
                  }}>REC</div>
                )}
                {phase === 'joined' && hasStream && proctorHint && (
                  <div style={{
                    position: 'absolute', top: 10, right: 10,
                    background: proctorHint.idle
                      ? 'rgba(51,65,85,0.92)'
                      : (proctorHint.ok ? 'rgba(5,150,105,0.92)' : 'rgba(185,28,28,0.92)'),
                    color: '#fff', fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 6,
                  }}>
                    {proctorHint.label}
                  </div>
                )}
                {(!hasStream || !cameraOn) && (
                  <div style={{
                    position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center', color: '#64748b', gap: 8,
                  }}>
                    <Video size={36} />
                    <div>Camera preview will appear here</div>
                  </div>
                )}
              </div>

              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 12, color: '#94a3b8', display: 'flex', justifyContent: 'space-between' }}>
                  <span>Microphone level</span>
                  <span>{micOk ? 'Detected' : 'Speak to test'}</span>
                </div>
                <div style={styles.meterTrack}>
                  <div style={{
                    width: `${micLevel}%`, height: '100%',
                    background: micOk ? '#22c55e' : '#38bdf8',
                    transition: 'width 80ms linear',
                  }} />
                </div>
              </div>

              {phase === 'setup' && (
                <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
                  <button type="button" onClick={enableDevices} style={{ ...styles.btn, flex: 1, background: '#1d4ed8', color: '#fff' }}>
                    Enable camera & mic
                  </button>
                  <button type="button" onClick={() => saveSetup().catch(() => {})} disabled={!setupReady || saving} style={{
                    ...styles.btn, flex: 1, background: setupReady ? '#065f46' : '#1e293b', color: '#fff',
                    opacity: setupReady ? 1 : 0.6, cursor: setupReady ? 'pointer' : 'not-allowed',
                  }}>
                    {saving ? 'Saving…' : 'Complete setup'}
                  </button>
                </div>
              )}

              {phase === 'joined' && (
                <button
                  type="button"
                  onClick={leaveRoom}
                  disabled={leaving}
                  style={{
                    ...styles.btn, marginTop: 18, background: '#7f1d1d', color: '#fff',
                    opacity: leaving ? 0.7 : 1, cursor: leaving ? 'wait' : 'pointer',
                  }}
                >
                  {leaving ? 'Leaving…' : 'Leave room'}
                </button>
              )}
            </section>

            <aside style={styles.card}>
              {phase === 'joined' ? (
                <MeetingInterviewPanel
                  token={token}
                  videoEl={videoRef}
                  micOn={micOn}
                  cameraOn={cameraOn}
                  streamRef={streamRef}
                  micLevel={micLevel}
                  onMicLevel={setMicLevel}
                  onProctorStatus={(status) => {
                    const next = hintFromProctor(status);
                    setProctorHint((prev) => {
                      if (!prev && !next) return prev;
                      if (prev?.ok === next?.ok && prev?.label === next?.label) return prev;
                      return next;
                    });
                  }}
                  hasStream={hasStream}
                  scheduledAt={room.scheduled_at}
                  onInterviewComplete={openThankYou}
                />
              ) : (
                <>
                  <div style={{ fontSize: 12, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.6 }}>Interview schedule</div>
                  <div style={{ fontSize: 20, fontWeight: 800, margin: '8px 0 4px' }}>{formatWhen(room)}</div>
                  <div style={{ color: '#94a3b8', fontSize: 13 }}>
                    {room.duration_min} min spoken
                    {room.coding_extra_min
                      ? ` · ${room.duration_min + room.coding_extra_min} min on the clock with coding`
                      : ''}
                    {' · '}Room {room.room_code}
                  </div>

                  <div style={{
                    marginTop: 16, padding: 12, borderRadius: 10,
                    background: lateGrace
                      ? 'rgba(251,191,36,0.12)'
                      : timeReached ? 'rgba(34,197,94,0.12)' : 'rgba(37,99,235,0.12)',
                    border: `1px solid ${lateGrace
                      ? 'rgba(251,191,36,0.4)'
                      : timeReached ? 'rgba(34,197,94,0.35)' : 'rgba(37,99,235,0.35)'}`,
                    fontSize: 13,
                  }}>
                    {waitMessage(msLeft, room.can_join, { msUntilClose })}
                    <div style={{ color: '#94a3b8', marginTop: 4 }}>
                      {setupReady
                        ? (lateGrace
                          ? `Join now and the interview starts from the first question. The room closes ${lateMin} minutes after the scheduled start.`
                          : 'Keep this tab open. The AI interview will start at the scheduled time — no refresh needed.')
                        : `Enable camera and mic first. You can enter ${room.join_early_minutes || 15} minutes early. Late join is allowed for ${lateMin} minutes — after that the room closes.`}
                    </div>
                  </div>

                  <ul style={{ paddingLeft: 18, fontSize: 13, lineHeight: 1.7, color: '#cbd5e1' }}>
                    <li style={{ color: cameraOk ? '#86efac' : '#cbd5e1' }}>{cameraOk ? 'Camera ready' : 'Camera not checked'}</li>
                    <li style={{ color: micOk ? '#86efac' : '#cbd5e1' }}>{micOk ? 'Microphone ready' : 'Microphone not checked'}</li>
                    <li>Quiet, well-lit room</li>
                    <li>Stay on this tab during the interview</li>
                    <li>This session is recorded and stored after you finish</li>
                    <li>Speak your answers. Pause 5 seconds to continue, or say “that’s all”, “I am done with my answer”, or “skip this question”</li>
                    <li>If you join up to {lateMin} minutes late, the interview still starts from question 1</li>
                  </ul>

                  <button
                    type="button"
                    onClick={joinRoom}
                    disabled={!room.can_join || !setupReady || joining}
                    style={{
                      ...styles.btn,
                      marginTop: 8,
                      background: room.can_join && setupReady ? '#2563eb' : '#1e293b',
                      color: '#fff',
                      opacity: room.can_join && setupReady ? 1 : 0.55,
                      cursor: room.can_join && setupReady ? 'pointer' : 'not-allowed',
                    }}
                  >
                    {joining ? (timeReached ? 'Starting…' : 'Joining…') : (room.can_join && !timeReached ? 'Enter waiting room' : joinLabel)}
                  </button>
                </>
              )}
            </aside>
          </div>
        )}
      </div>
      <style>{`
        @media (max-width: 840px) {
          .meeting-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}
